import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { API_BASE_URL, apiFetch, isDemoMode } from './config';
import MessageContent from './components/MessageContent';

// OpenAI API key for subject matching

const StruggleMomentsDashboard = ({ username: propUsername, onClose }) => {
  const [struggleMoments, setStruggleMoments] = useState([]);
  const [visibleCount, setVisibleCount] = useState(8); // Show 8 items initially
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [existingSubjects, setExistingSubjects] = useState([]);
  const [subjectsLoaded, setSubjectsLoaded] = useState(false);
  const hasProcessedLogs = useRef(false);
  const [lastClickedIndex, setLastClickedIndex] = useState(null); // Track last clicked row
  const [hoveredIndex, setHoveredIndex] = useState(null); // Track hovered row
  
  // Prevent body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);
  
  // Reviewed struggles - stored in localStorage
  const [reviewedStruggles, setReviewedStruggles] = useState(() => {
    const username = propUsername || localStorage.getItem('username') || 'default';
    const saved = localStorage.getItem(`reviewedStruggles_${username}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  // Helper to create unique ID for a struggle moment
  const getStruggleId = (moment) => `${moment.topic}_${moment.date}`;
  
  // Toggle reviewed status
  const toggleReviewed = (moment, e) => {
    e.stopPropagation(); // Prevent opening the detail view
    const username = propUsername || localStorage.getItem('username') || 'default';
    const id = getStruggleId(moment);
    let updated;
    if (reviewedStruggles.includes(id)) {
      updated = reviewedStruggles.filter(r => r !== id);
    } else {
      updated = [...reviewedStruggles, id];
    }
    setReviewedStruggles(updated);
    localStorage.setItem(`reviewedStruggles_${username}`, JSON.stringify(updated));
  };

  // Use prop username or fallback to localStorage
  const username = propUsername || localStorage.getItem('username');

  // Fetch existing subjects from mastery data
  useEffect(() => {
    const fetchExistingSubjects = async () => {
      if (!username || isDemoMode()) {
        setSubjectsLoaded(true);
        return;
      }
      try {
        const response = await apiFetch(`${API_BASE_URL}/api/studentMastery?username=${encodeURIComponent(username)}`);
        if (response.ok) {
          const data = await response.json();
          console.log("Raw mastery API response:", data);
          // Handle both array format and object with history/mastery property
          const dataArray = Array.isArray(data) ? data : (data.history || data.mastery || []);
          const subjects = dataArray.map(m => m.subject).filter(Boolean);
          console.log("Loaded existing subjects for matching:", subjects);
          setExistingSubjects(subjects);
        }
      } catch (error) {
        console.error("Error fetching existing subjects:", error);
      } finally {
        setSubjectsLoaded(true);
      }
    };
    fetchExistingSubjects();
  }, [username]);

  // Function to match subject based on question/feedback context using LLM
  const matchSubjectWithQuestion = async (rawSubject, questionText, feedbackText, subjects) => {
    console.log(`matchSubjectWithQuestion called: raw="${rawSubject}", question="${questionText?.substring(0,50)}...", feedback="${feedbackText?.substring(0,50)}...", subjects=`, subjects);
    
    // Check for valid question/context text (more than just "..." or empty)
    const hasValidQuestion = questionText && questionText.length > 10 && questionText !== '...';
    const hasValidFeedback = feedbackText && feedbackText.length > 10;
    
    if (!subjects || subjects.length === 0) {
      console.log("No subjects available, returning raw subject");
      return rawSubject;
    }
    
    // Check for exact match first
    const exactMatch = subjects.find(
      s => s.toLowerCase() === rawSubject.toLowerCase()
    );
    if (exactMatch) {
      console.log("Exact match found:", exactMatch);
      return exactMatch;
    }
    
    // Use question if available, otherwise use feedback
    const contextText = hasValidQuestion ? questionText : (hasValidFeedback ? feedbackText : '');
    const contextType = hasValidQuestion ? "Question" : "Feedback";
    
    // If no valid question or feedback, try simple keyword matching as fallback
    if (!hasValidQuestion && !hasValidFeedback) {
      console.log("No valid question or feedback context, returning raw subject");
      return rawSubject;
    }
    
    // SIMPLE KEYWORD MATCHING FALLBACK - check feedback for obvious keywords
    const lowerContext = contextText.toLowerCase();
    for (const subject of subjects) {
      const lowerSubject = subject.toLowerCase();
      // Check for the subject name or key terms in the context
      if (lowerContext.includes(lowerSubject)) {
        console.log(`Keyword match found: "${subject}" in ${contextType.toLowerCase()}`);
        return subject;
      }
      // Check for common variations
      if (lowerSubject.includes('print') && (lowerContext.includes('print(') || lowerContext.includes('print function') || lowerContext.includes('print statement'))) {
        console.log(`Keyword match found: "${subject}" via print keyword`);
        return subject;
      }
      if (lowerSubject.includes('loop') && (lowerContext.includes('for loop') || lowerContext.includes('while loop') || lowerContext.includes('loop '))) {
        console.log(`Keyword match found: "${subject}" via loop keyword`);
        return subject;
      }
      if (lowerSubject.includes('input') && lowerContext.includes('input(')) {
        console.log(`Keyword match found: "${subject}" via input keyword`);
        return subject;
      }
    }
    
    // If keyword matching didn't work, try LLM
    
    try {
      console.log(`Calling LLM to match subject using ${contextType}...`);
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `You are a subject matcher for a Python learning app. Given a quiz context (question or feedback) and a list of EXISTING subjects, determine the correct subject for this quiz.

Rules:
1. Analyze the context to understand what Python topic is actually being tested.
2. If the context clearly relates to one of the EXISTING subjects, return that EXISTING subject name.
3. Look for keywords like "print", "input", "loop", "for", "while", "if", "function", etc.
4. Only if no existing subject matches, return the original subject.
5. Return ONLY the subject name, nothing else.

Examples:
- Context: "The print function requires text in quotation marks", Existing: ["Print Function", "Variables"] → Print Function
- Context: "The correct answer uses input()", Existing: ["Input Function", "Print Function"] → Input Function
- Context: "This for loop iterates...", Existing: ["Loops", "Print Function"] → Loops`
            },
            {
              role: 'user',
              content: `${contextType}: "${contextText.substring(0, 500)}"

Original Subject (possibly wrong): "${rawSubject}"

Existing subjects: ${JSON.stringify(subjects)}

Based on the ${contextType.toLowerCase()} content, return the correct subject name.`
            }
          ],
          max_tokens: 50,
          temperature: 0
        })
      });
      
      if (!response.ok) {
        console.log("LLM API error:", response.status);
        return rawSubject;
      }
      
      const data = await response.json();
      const matchedSubject = data.choices[0]?.message?.content?.trim();
      console.log("LLM returned:", matchedSubject);
      
      if (matchedSubject) {
        const validMatch = subjects.find(
          s => s.toLowerCase() === matchedSubject.toLowerCase()
        );
        if (validMatch) {
          console.log(`Subject matched: "${rawSubject}" → "${validMatch}" based on question`);
          return validMatch;
        }
      }
      return rawSubject;
    } catch (error) {
      console.error("Error matching subject:", error);
      return rawSubject;
    }
  };

  useEffect(() => {
    // Only fetch logs once subjects have been loaded
    if (!subjectsLoaded || hasProcessedLogs.current) return;
    
    const fetchQuizResults = async () => {
      hasProcessedLogs.current = true; // Prevent re-running
      if (isDemoMode()) {
        setIsDemo(true);
        setLoading(false);
        return;
      }
      console.log("=== STRUGGLE DASHBOARD DEBUG ===");
      console.log("propUsername:", propUsername);
      console.log("localStorage username:", localStorage.getItem('username'));
      console.log("Final username being used:", username);
      console.log("existingSubjects:", existingSubjects);
      setLoading(true);
      try {
        // Fetch from new quiz-result API (optimized - only quiz results, no filtering needed)
        const url = `${API_BASE_URL}/api/quiz-result?username=${encodeURIComponent(username)}&limit=500`;
        console.log("Fetching URL:", url);
        const response = await apiFetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch quiz results');
        }
        const data = await response.json();
        console.log("Quiz results fetched successfully:", data);
        
        // Process quiz results directly (no parsing needed - data is already structured)
        const quizResults = data.quiz_results || [];
        const moments = quizResults
          .filter(qr => qr.result === 'wrong') // Only show wrong answers in Past Quiz Review
          .map(qr => ({
            topic: qr.subject,
            date: qr.created_at,
            question: qr.question || '',
            feedback: qr.feedback || '',
            userAnswer: qr.user_answer || ''
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
        
        console.log("Identified struggle moments:", moments.length);
        setStruggleMoments(moments);
      } catch (error) {
        console.error("Error fetching quiz results:", error);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch after we've loaded existing subjects
    if (username) {
      fetchQuizResults();
    } else {
      console.warn("Username is missing. Cannot fetch data.");
      setLoading(false);
    }
  }, [username, subjectsLoaded]); // Depend on subjectsLoaded, not existingSubjects

  const identifyStruggleMoments = async (logs, subjects) => {
    console.log("Processing logs to identify struggles:", logs.length, "with subjects:", subjects);
    const moments = [];
    
    // Sort logs by date to ensure chronological order
    const sortedLogs = [...logs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    // Debug: Show all logs with their tutorMessage content
    console.log("=== ALL LOGS DEBUG ===");
    sortedLogs.forEach((log, i) => {
      const hasQuizResult = log.tutorMessage?.includes('[QUIZ_RESULT]');
      const preview = log.tutorMessage?.substring(0, 100) || '(empty)';
      console.log(`Log ${i}: hasQUIZ_RESULT=${hasQuizResult}, preview="${preview}..."`);
    });
    console.log("=== END LOGS DEBUG ===");
    
    // Find logs that contain [QUIZ_RESULT] with Result: wrong
    for (let index = 0; index < sortedLogs.length; index++) {
      const log = sortedLogs[index];
      
      // Debug: Check all logs with QUIZ_RESULT
      if (log.tutorMessage && log.tutorMessage.includes('[QUIZ_RESULT]')) {
        console.log(`Found QUIZ_RESULT in log ${index}:`, log.tutorMessage.substring(0, 200) + '...');
        // Parse the QUIZ_RESULT tag content (same format as App.js)
        const quizMatch = log.tutorMessage.match(/\[QUIZ_RESULT\]([\s\S]*?)\[\/QUIZ_RESULT\]/);
        if (!quizMatch) continue;
        
        const quizContent = quizMatch[1];
        console.log(`QUIZ_RESULT content:`, quizContent);
        
        // Updated regex to stop at newline for Subject
        const subjectMatch = quizContent.match(/Subject:\s*([^\n]+)/i);
        const resultMatch = quizContent.match(/Result:\s*(wrong)/i);
        // NEW: Try to extract Question directly from QUIZ_RESULT tag (100% reliable)
        const questionMatch = quizContent.match(/Question:\s*([\s\S]+?)(?=\[\/QUIZ_RESULT\]|$)/i);
        
        console.log(`Parsed: subject="${subjectMatch?.[1]}", result="${resultMatch?.[1]}", hasQuestion=${!!questionMatch}`);
        
        if (resultMatch && subjectMatch) {
          const rawTopic = subjectMatch[1].trim();
          
          // Extract the feedback (text before QUIZ_RESULT tag)
          const feedback = log.tutorMessage.split('[QUIZ_RESULT]')[0].trim();
          
          // FIRST: Try to get question directly from QUIZ_RESULT tag (new format - 100% reliable)
          let questionText = '';
          if (questionMatch) {
            questionText = questionMatch[1].trim();
            console.log(`✅ Found question directly in QUIZ_RESULT tag: "${questionText.substring(0, 50)}..."`);
          }
          
          // FALLBACK: If question not in tag, try the old backward search method
          if (!questionText || questionText.length < 10) {
            console.log(`⚠️ Question not found in QUIZ_RESULT tag, using fallback search...`);
            const feedbackLower = feedback.toLowerCase();
            
            let bestQuestion = '';
            let bestMatchScore = 0;
            
            // Extract code snippets from feedback for matching
            // Look for patterns like: print("Hello, World!"), print('text'), etc.
            const feedbackCodeSnippets = feedback.match(/(?:print|input|for|while|if|def)\s*\([^)]*\)/gi) || [];
            // Also look for the answer option pattern like "C) print(...)"
            const answerOptionMatch = feedback.match(/[A-D]\)\s*([^\n]+)/gi) || [];
            
            console.log("Feedback code snippets:", feedbackCodeSnippets);
            console.log("Answer option matches:", answerOptionMatch);
            
            for (let i = index - 1; i >= 0 && i >= index - 30; i--) {
              const prevLog = sortedLogs[i];
              if (!prevLog.tutorMessage || !prevLog.tutorMessage.includes('?')) continue;
              
              const msgLower = prevLog.tutorMessage.toLowerCase();
              
              // Calculate a match score based on shared content
              let matchScore = 0;
              
              // Check if this looks like a quiz question (has options)
              const hasOptions = /[A-D]\)|[1-4]\.|Option [A-D]|Choice [1-4]/i.test(prevLog.tutorMessage);
              if (hasOptions) matchScore += 3;
              
              // STRONG MATCH: Check if feedback mentions a specific answer option that exists in the question
              // This is the most reliable indicator that this is the correct question
              for (const opt of answerOptionMatch) {
                // Extract just the code part after "C) " etc.
                const optCode = opt.replace(/^[A-D]\)\s*/, '').trim();
                if (optCode.length > 5 && prevLog.tutorMessage.includes(optCode)) {
                  matchScore += 50; // Very strong match - the answer option is in the question
                  console.log(`Found answer option "${optCode}" in question`);
                }
              }
              
              // Check for EXACT code snippet matches from feedback
              for (const snippet of feedbackCodeSnippets) {
                if (prevLog.tutorMessage.includes(snippet)) {
                  matchScore += 30; // Strong match - exact code snippet found
                  console.log(`Found exact code snippet "${snippet}" in question`);
                }
              }
              
              // Check for matching string content (like "Hello, World!")
              const stringMatches = feedback.match(/"[^"]+"|'[^']+'/g) || [];
              for (const str of stringMatches) {
                if (prevLog.tutorMessage.includes(str)) {
                  matchScore += 20; // Good match - same string literal
                }
                // Penalty if feedback has this string but question doesn't
                if (!prevLog.tutorMessage.includes(str)) {
                  matchScore -= 10; // Penalty for missing string content from feedback
                }
              }
              
              // Penalty: if question has code patterns not mentioned in feedback
              const questionCodeSnippets = prevLog.tutorMessage.match(/(?:print|input|for|while|if|def)\s*\([^)]*\)/gi) || [];
              for (const qSnippet of questionCodeSnippets) {
                // If question has a code snippet that feedback doesn't reference, slight penalty
                const snippetInFeedback = feedbackCodeSnippets.some(fSnippet => 
                  fSnippet.toLowerCase() === qSnippet.toLowerCase()
                );
                if (!snippetInFeedback && questionCodeSnippets.length > 0 && feedbackCodeSnippets.length > 0) {
                  matchScore -= 5; // Small penalty for unrelated code in question
                }
              }
              
              if (matchScore > bestMatchScore) {
                bestMatchScore = matchScore;
                bestQuestion = prevLog.tutorMessage;
                console.log(`New best question found with score ${matchScore}`);
              }
            }
            
            // Only use the question if we found a reasonable match
            // Score of 3 = has quiz options, higher = better content match
            if (bestMatchScore >= 3) {
              questionText = bestQuestion;
              console.log(`Found matching question via fallback with score ${bestMatchScore}`);
            } else {
              console.log(`No matching question found (best score: ${bestMatchScore})`);
              questionText = ''; // Don't show a mismatched question
            }
          } // End of fallback search
          
          console.log(`Processing "${rawTopic}": question found=${questionText && questionText.length > 20}, feedback length=${feedback.length}`);
          
          // Match the subject using question AND feedback context - pass subjects parameter
          const topic = await matchSubjectWithQuestion(rawTopic, questionText, feedback, subjects);
          
          moments.push({
            topic: topic,
            date: log.created_at,
            question: questionText,
            feedback: feedback,
            userAnswer: log.userMessage || '',
          });
        }
      }
    }

    console.log("Identified struggle moments:", moments.length);
    // Sort moments by date, most recent first
    setStruggleMoments(moments.sort((a, b) => new Date(b.date) - new Date(a.date)));
  };

  // Helper to render text with proper Python code formatting
  // Preprocesses stored quiz text to add markdown code fences, then uses MessageContent (same as chat)
  const renderFormattedText = (text) => {
    if (!text) return null;

    // If text already has code fences, just pass through to MessageContent
    if (text.includes('```')) {
      return <MessageContent content={text} />;
    }

    // Multi-line text that already has structure (newlines present) - check if code lines exist
    const lines = text.split('\n');
    if (lines.length >= 3) {
      // Check if any lines look like code (indented or starts with Python keyword)
      const codePattern = /^(\s{2,}|\t)\S|^(def |class |if |elif |else:|for |while |import |from |return |print\(|input\(|try:|except|finally:|with |raise |assert |yield |lambda |\w+\s*=\s*|\w+\s*[+\-*/]=|\w+\[|\w+\.\w+\(|break$|continue$|pass$)/;
      const hasCodeLines = lines.some(l => codePattern.test(l.trim()));
      
      if (hasCodeLines) {
        // Separate text lines from code lines, wrap code in fences
        let result = '';
        let inCode = false;
        let codeBuffer = [];
        
        for (const line of lines) {
          const trimmed = line.trim();
          const isCode = trimmed && (codePattern.test(trimmed) || (inCode && /^\s{2,}\S/.test(line)));
          
          if (isCode && !inCode) {
            inCode = true;
            codeBuffer = [line];
          } else if (isCode && inCode) {
            codeBuffer.push(line);
          } else if (!isCode && inCode) {
            // Flush code buffer
            result += '\n```python\n' + codeBuffer.join('\n') + '\n```\n';
            codeBuffer = [];
            inCode = false;
            result += line + '\n';
          } else {
            result += line + '\n';
          }
        }
        if (inCode && codeBuffer.length > 0) {
          result += '\n```python\n' + codeBuffer.join('\n') + '\n```\n';
        }
        return <MessageContent content={result.trim()} />;
      }
      // No code lines - just render as-is
      return <MessageContent content={text} />;
    }

    // === Flat single-line text (LLM flattened the code) ===
    // Extract answer options first
    let mainText = text;
    let optionsPart = '';
    const optMatch = mainText.match(/\s(A\)\s)/);
    if (optMatch && /B\)\s/.test(mainText.slice(optMatch.index))) {
      optionsPart = mainText.slice(optMatch.index).trim();
      mainText = mainText.slice(0, optMatch.index).trim();
      // Put each option on its own line
      optionsPart = optionsPart.replace(/\s+([A-D]\)\s)/g, '\n$1').trim();
    }

    // Split question text from code at "?" boundary
    let questionPart = '';
    let codePart = '';
    const qMark = mainText.lastIndexOf('?');
    if (qMark >= 0 && qMark < mainText.length - 1) {
      questionPart = mainText.slice(0, qMark + 1).trim();
      codePart = mainText.slice(qMark + 1).trim();
    } else {
      const codeStart = mainText.match(/(?:^|\s)(\w+\s*=\s*(?:\[.*?\]|\(.*?\)|\{.*?\}|[\d'"]))|(for\s+\w+\s+in\s+)|(while\s+)|(print\s*\()|(def\s+)|(if\s+\w)/);
      if (codeStart && codeStart.index > 10) {
        questionPart = mainText.slice(0, codeStart.index).trim();
        codePart = mainText.slice(codeStart.index).trim();
      } else {
        questionPart = mainText;
      }
    }

    // Reconstruct Python code from flat text
    if (codePart) {
      let code = codePart;
      // Insert newlines before Python keywords
      code = code.replace(/\s+(for\s+\w+\s+in\s+)/g, '\n$1');
      code = code.replace(/\s+(while\s+\w)/g, '\n$1');
      code = code.replace(/\s+(if\s+\w)/g, '\n$1');
      code = code.replace(/\s+(elif\s+)/g, '\n$1');
      code = code.replace(/\s+(else\s*:)/g, '\n$1');
      code = code.replace(/\s+(def\s+\w)/g, '\n$1');
      code = code.replace(/\s+(class\s+\w)/g, '\n$1');
      code = code.replace(/\s+(return[\s(])/g, '\nreturn ');
      code = code.replace(/\s+(print\s*\()/g, '\nprint(');
      code = code.replace(/\s+(input\s*\()/g, '\ninput(');
      code = code.replace(/\s+(try\s*:)/g, '\n$1');
      code = code.replace(/\s+(except[\s:])/g, '\n$1');
      code = code.replace(/(?<=\S)\s+(break)(?=\s|$)/g, '\nbreak');
      code = code.replace(/(?<=\S)\s+(continue)(?=\s|$)/g, '\ncontinue');
      code = code.replace(/:\s+(\w+\s*[+\-*/]?=\s)/g, ':\n$1');
      code = code.replace(/(\S)\s+(\w+\s*=\s*(?!\s*=))/g, '$1\n$2');

      // Split inline bodies: "if x > 5: print('big')" -> two lines
      const expanded = [];
      code.split('\n').forEach(raw => {
        const stripped = raw.trim();
        const inlineMatch = stripped.match(/^((?:if|elif|else|for|while|try|except|finally|with|def|class)\b[^:]*:)\s+(.+)$/);
        if (inlineMatch) {
          expanded.push(inlineMatch[1]);
          expanded.push(inlineMatch[2]);
        } else if (stripped) {
          expanded.push(stripped);
        }
      });

      // Add indentation based on Python structure
      const result = [];
      let indent = 0;
      expanded.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        if (/^(elif |else\s*:|except|finally\s*:)/.test(trimmed)) indent = Math.max(0, indent - 1);
        result.push('    '.repeat(indent) + trimmed);
        if (trimmed.endsWith(':')) indent++;
        else if (/^(return\b|break$|continue$|pass$)/.test(trimmed)) indent = Math.max(0, indent - 1);
      });
      codePart = result.join('\n');
    }

    // Build markdown string with code fences and pass to MessageContent
    let markdown = '';
    if (questionPart) markdown += questionPart + '\n\n';
    if (codePart) markdown += '```python\n' + codePart + '\n```\n\n';
    if (optionsPart) markdown += optionsPart + '\n';
    
    return <MessageContent content={markdown.trim()} />;
  };

  // Render the detail view when a moment is selected
  if (selectedMoment) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{ width: '70%', maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
          <button onClick={() => setSelectedMoment(null)} className="close-modal-btn" style={{ float: 'right' }}>×</button>
          <h2>Quiz Review: {selectedMoment.topic}</h2>
          <p style={{ color: '#888', fontSize: '14px' }}>
            Date: {new Date(selectedMoment.date).toLocaleString()}
          </p>
          
          {selectedMoment.question ? (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ color: '#58a6ff', marginBottom: '10px' }}>📝 Quiz Question:</h3>
              <div style={{ 
                backgroundColor: '#1e1e1e', 
                padding: '8px 6px', 
                borderRadius: '8px',
                lineHeight: '1.6',
                color: '#e0e0e0',
                borderLeft: '3px solid #58a6ff'
              }}>
                {renderFormattedText(selectedMoment.question)}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ color: '#58a6ff', marginBottom: '10px' }}>📝 Quiz Question:</h3>
              <div style={{ 
                backgroundColor: '#1e1e1e', 
                padding: '15px', 
                borderRadius: '8px',
                color: '#888',
                fontStyle: 'italic',
                borderLeft: '4px solid #58a6ff'
              }}>
                Quiz question not available in conversation history. See the feedback below for context.
              </div>
            </div>
          )}
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ color: '#f0883e', marginBottom: '10px' }}>💬 Your Answer:</h3>
            <div style={{ 
              backgroundColor: '#1e1e1e', 
              padding: '8px 6px', 
              borderRadius: '8px',
              color: '#e0e0e0',
              borderLeft: '3px solid #f0883e'
            }}>
              {renderFormattedText(selectedMoment.userAnswer || "No answer recorded")}
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h3 style={{ color: '#f85149', marginBottom: '10px' }}>✅ Correct Answer & Feedback:</h3>
            <div style={{ 
              backgroundColor: '#1e1e1e', 
              padding: '8px 6px', 
              borderRadius: '8px',
              lineHeight: '1.6',
              color: '#e0e0e0',
              borderLeft: '3px solid #f85149'
            }}>
              {renderFormattedText(selectedMoment.feedback || "No feedback available")}
            </div>
          </div>

          <button 
            onClick={() => setSelectedMoment(null)} 
            style={{ 
              marginTop: '20px', 
              padding: '10px 20px', 
              backgroundColor: '#238636',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            ← Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '60%', maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <button onClick={() => { window.scrollTo(0, 0); onClose(); }} className="close-modal-btn" style={{ float: 'right' }}>×</button>
        <h2 style={{ color: '#00d4aa' }}>📝 Past Quiz Review</h2>
        <p>Click on any item to review the quiz details.</p>
        <div className="struggle-list-container" style={{ flex: 1, overflowY: 'auto', maxHeight: '60vh', paddingRight: '10px' }}>
          {loading ? (
            <p>Loading...</p>
          ) : struggleMoments.length > 0 ? (
            <>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {struggleMoments.slice(0, visibleCount).map((moment, index) => {
                  const isReviewed = reviewedStruggles.includes(getStruggleId(moment));
                  const isLastClicked = lastClickedIndex === index;
                  const isHovered = hoveredIndex === index;
                  return (
                    <li 
                      key={index} 
                      onClick={() => {
                        console.log("Clicked moment:", moment);
                        setLastClickedIndex(index);
                        setSelectedMoment(moment);
                      }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px 15px', 
                        backgroundColor: isLastClicked ? '#3b82f630' : (isHovered ? '#404040' : (isReviewed ? '#22c55e20' : '#2d2d2d')),
                        borderRadius: '6px',
                        marginBottom: '8px',
                        color: isReviewed ? '#6b7280' : '#ffffff',
                        cursor: 'pointer',
                        borderLeft: isLastClicked ? '4px solid #3b82f6' : (isHovered ? '4px solid #888' : (isReviewed ? '4px solid #22c55e' : '4px solid transparent')),
                        boxShadow: isLastClicked ? '0 0 0 1px #3b82f6' : 'none',
                        transition: 'background-color 0.15s, border-left 0.15s'
                      }}
                    >
                      <span style={{ pointerEvents: 'none', textDecoration: isReviewed ? 'line-through' : 'none' }}>
                        <strong>Topic:</strong> {moment.topic}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ pointerEvents: 'none' }}>
                          <strong>Date:</strong> {moment.date ? new Date(moment.date).toLocaleDateString() : 'N/A'}
                        </span>
                        <button
                          onClick={(e) => toggleReviewed(moment, e)}
                          title={isReviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: isReviewed ? '#22c55e' : 'transparent',
                            color: isReviewed ? '#fff' : '#6b7280',
                            border: isReviewed ? 'none' : '1px solid #6b7280',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {isReviewed ? '✓ Reviewed' : '✓'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {visibleCount < struggleMoments.length && (
                <button 
                  onClick={() => setVisibleCount(prevCount => prevCount + 8)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '15px',
                    backgroundColor: '#37373d',
                    color: 'white',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Load More
                </button>
              )}
            </>
          ) : isDemo ? (
            <div style={{ textAlign: 'left', padding: '40px 20px', color: '#fffacd', whiteSpace: 'pre-wrap' }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📝</span>
              <h3 style={{ color: '#fffacd', fontSize: '18px', marginBottom: '12px' }}>Past Quiz Review — Locked in Demo Mode</h3>
              {`With a real account, every quiz you take gets saved here so you can revisit what you got wrong and study the gaps.

Each entry shows:
  ❌  The question you answered incorrectly
  💬  The AI's feedback explaining why your answer was wrong
  📖  A one-click option to ask the tutor to re-explain that topic

Example entry:
  Topic: For Loops  •  June 18
  Question: "What does range(1, 5) produce?"
  Your answer: [1, 2, 3, 4, 5]
  Feedback: "range(1, 5) stops before 5, so it produces [1, 2, 3, 4].
             The second argument is exclusive."

Over time this list becomes your personal study guide — sorted by most recent mistake.

✨ Sign up to start building your quiz history!`}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📊</span>
              <h3 style={{ color: '#fff', fontSize: '18px' }}>No Data Available Yet</h3>
              <p>Your quiz review will appear here after you've taken a few quizzes.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StruggleMomentsDashboard;
